import React, { createContext, useContext, useEffect, useState } from "react";

export type VideoMode = "learning" | "showroom" | "local" | "showcase";

interface VideoModeContextValue {
  mode: VideoMode;
  setMode: (mode: VideoMode) => void;
}

const VideoModeContext = createContext<VideoModeContextValue | null>(null);

// Hilfsfunktion: Prüft, ob Advanced Mode aktiviert ist (ohne Hook-Abhängigkeit)
function isAdvancedModeEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    // Prüfe localStorage direkt (konsistent mit AdvancedModeContext)
    const saved = localStorage.getItem("hustle-advanced-mode");
    if (saved === "true") return true;

    // Fallback: Prüfe user_private.settings (wenn verfügbar)
    // Da wir hier keinen async Zugriff haben, nutzen wir nur localStorage
    return false;
  } catch {
    return false;
  }
}

export function VideoModeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = useState<VideoMode>(() => {
    const saved = localStorage.getItem("video-mode");
    if (
      saved === "learning" ||
      saved === "showroom" ||
      saved === "local" ||
      saved === "showcase"
    ) {
      const s = saved as VideoMode;
      // Lernmodus nur mit Advanced Mode; andere Modi aus localStorage
      if (!isAdvancedModeEnabled() && s === "learning") {
        return "showroom";
      }
      return s;
    }
    return "showroom";
  });

  // Ohne Advanced: Lernmodus-Zustand zurück (local/showroom/showcase bleiben erhalten)
  useEffect(() => {
    const checkAdvancedMode = () => {
      if (!isAdvancedModeEnabled() && mode === "learning") {
        setModeState("showroom");
        try {
          localStorage.setItem("video-mode", "showroom");
        } catch {
          /* ignore */
        }
      }
    };

    checkAdvancedMode();

    const interval = setInterval(checkAdvancedMode, 500);

    window.addEventListener("storage", checkAdvancedMode);

    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", checkAdvancedMode);
    };
  }, [mode]);

  const setMode = (newMode: VideoMode) => {
    if (newMode === "learning" && !isAdvancedModeEnabled()) {
      return;
    }
    setModeState(newMode);
    localStorage.setItem("video-mode", newMode);
  };

  /** Ohne Advanced: Lernmodus nicht anwenden (Anzeige + Queries wie Showcase). */
  const effectiveMode: VideoMode =
    !isAdvancedModeEnabled() && mode === "learning" ? "showcase" : mode;

  return (
    <VideoModeContext.Provider value={{ mode: effectiveMode, setMode }}>
      {children}
    </VideoModeContext.Provider>
  );
}

export function useVideoMode() {
  const context = useContext(VideoModeContext);
  if (!context) {
    throw new Error(
      "useVideoMode muss innerhalb von VideoModeProvider verwendet werden"
    );
  }
  return context;
}
